import { getCoverUrl, getEpubDownloadUrl } from "#/client";
import { useRequireAuth } from "#/feature/auth/use-require-auth";
import { useTts } from "#/feature/tts-playback/tts-context";
import { useSignal } from "#/use-signal";
import { autoFollowSignal, privacySignal } from "#/feature/tts-playback/tts-store";
import { ArrowLeft, ArrowRight, BookOpen, Download, ExternalLink, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { type TtsSegment } from "./chapter-utils";
import { applyPatterns, getPatterns } from "#/feature/replace/replace-utils";
import { Marker } from "#/types";
import { Button } from "#/components/button/button";
import { Input } from "#/components/input/input";
import {
  containerClass,
  headerContainerClass,
  navSectionSideStyles,
  navSectionCenterClass,
  titleButton,
  marqueeStyles,
  dropdownMenuClass,
  menuItemClass,
  menuItemLiClass,
  menuItemStyles,
  contentClass,
  contentInnerClass,
  paragraphClass,
  sentence,
  dialogOverlayClass,
  dialogBoxClass,
  dialogHeaderClass,
  dialogTitleClass,
  dialogDescriptionClass,
  dialogBodyClass,
  dialogLabelClass,
  dialogActionsClass,
  headerClass,
  headerInnerClass,
} from "./chapter-page.css";
import { useChapter } from "./use-chapter";
import { css } from "styled-system/css";

export function ChapterPage() {
  const allowed = useRequireAuth();
  const { chapter, book, loading, error, goToNext, goToPrev, handleDeleteFromHere, bookId } =
    useChapter();
  const activeSentenceRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [downloadLimit, setDownloadLimit] = useState(100);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const privacyMode = useSignal(privacySignal);
  const { play, isPlaying, currentChapterId, currentIndex } = useTts();

  // Close menu when clicking outside
  // TODO: Create a reusable dropdown/menu component to handle this generically
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  // Reset overflow check when title changes
  useEffect(() => {
    setIsOverflowing(false);
  }, [chapter?.title]);

  // Measure overflow after render
  useEffect(() => {
    if (!isOverflowing && titleRef.current) {
      const isCurrentlyOverflowing = titleRef.current.scrollWidth > titleRef.current.clientWidth;
      if (isCurrentlyOverflowing) {
        setIsOverflowing(true);
      }
    }
  }, [chapter?.title, isOverflowing]);

  // Use signal for autoFollow
  const autoFollow = useSignal(autoFollowSignal);

  const handlePrev = () => {
    goToPrev();
  };

  const handleNext = () => {
    goToNext();
  };

  const [processedSegments, setProcessedSegments] = useState<TtsSegment[]>([]);

  useEffect(() => {
    async function processSegments() {
      if (!chapter) {
        setProcessedSegments([]);
        return;
      }

      let contentSegments: TtsSegment[] = [];

      if (chapter.structured && chapter.structured.length > 0) {
        // Map backend markers to TtsSegment
        const mapped: TtsSegment[] = [];
        let inHeader = false;

        for (let i = 0; i < chapter.structured.length; i++) {
          const s = chapter.structured[i];
          if (!s) continue;

          const prevUnit = i > 0 ? chapter.structured[i - 1] : undefined;
          const prevMarker = prevUnit ? prevUnit.marker : null;

          if (s.marker === Marker.HeaderStart) {
            inHeader = true;
            continue;
          }
          if (s.marker === Marker.HeaderEnd) {
            inHeader = false;
            continue;
          }

          // Only process text carrying units or legacy markers
          if (s.text.trim().length > 0 || s.marker === Marker.ParagraphStart) {
            if (s.marker === Marker.ParagraphStart) {
              continue;
            }

            let isNewPara = false;
            if (i === 0) isNewPara = true;
            else if (prevMarker === Marker.ParagraphStart) isNewPara = true;
            else if (prevMarker === Marker.HeaderStart) isNewPara = true;

            mapped.push({
              text: s.text,
              isNewParagraph: isNewPara,
              isNewSentence:
                s.marker === Marker.SentenceStart ||
                s.marker === Marker.SentenceOverflow ||
                inHeader,
              isHeader: inHeader,
              sourceIndex: i,
            });
          }
        }

        // Filter out empty texts just in case
        const filtered = mapped.filter((s) => s.text.trim().length > 0);

        // Apply search/replace
        const patterns = await getPatterns();
        contentSegments = filtered.map((s) => ({
          ...s,
          text: applyPatterns(s.text, patterns, true),
        }));
      }

      const filteredSegments = contentSegments.filter((s) => s.text.trim().length > 0);
      setProcessedSegments(filteredSegments);
    }
    void processSegments();
  }, [chapter?.title, chapter?.structured]);

  // Group segments into paragraphs
  const paragraphGroups = useMemo(() => {
    const groups: { startIndex: number; segments: TtsSegment[] }[] = [];
    let currentGroup: TtsSegment[] = [];
    let currentStart = 0;

    processedSegments.forEach((s, i) => {
      if (s.isNewParagraph && i !== 0) {
        groups.push({ startIndex: currentStart, segments: currentGroup });
        currentGroup = [s];
        currentStart = i;
      } else {
        currentGroup.push(s);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ startIndex: currentStart, segments: currentGroup });
    }

    return groups;
  }, [processedSegments]);

  useEffect(() => {
    if (autoFollow && activeSentenceRef.current) {
      activeSentenceRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentIndex, currentChapterId, autoFollow, processedSegments]);

  if (!allowed) return null;

  if (loading) {
    return <div style={{ padding: "1rem", textAlign: "center" }}>Loading...</div>;
  }

  if (error || !chapter) {
    return (
      <div style={{ padding: "1rem", textAlign: "center", color: "var(--colors-error)" }}>
        Error: {error || "Chapter not found"}
      </div>
    );
  }

  const handleDownloadClick = () => {
    if (bookId) {
      const url = getEpubDownloadUrl(parseInt(bookId), chapter.id, downloadLimit);
      window.open(url, "_blank");
      setIsDownloadDialogOpen(false);
    }
  };

  const handlePlayChapter = (index = -1) => {
    if (bookId) {
      void play(
        parseInt(bookId),
        chapter.id,
        chapter.title,
        book?.title,
        book ? getCoverUrl(book.id) : undefined,
        index,
      );
    }
  };

  const isCurrentChapterPlaying = isPlaying && currentChapterId === chapter?.id;

  return (
    <div className={containerClass}>
      {/* Header Section */}
      <div className={headerContainerClass}>
        <div className={headerInnerClass}>
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={!chapter.prev_id}
            css={navSectionSideStyles}
            aria-label="Previous Chapter"
          >
            <ArrowLeft size={24} />
          </Button>

          <div className={navSectionCenterClass}>
            <div
              ref={menuRef}
              style={{ position: "relative", maxWidth: "100%" }}
              data-debug-id="chapter-title"
            >
              <Button
                variant="primary"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={titleButton({ active: true })}
                aria-label="Chapter Menu"
              >
                <span ref={titleRef} className={isOverflowing ? css(marqueeStyles) : undefined}>
                  {chapter.title}
                  {isOverflowing && (
                    <>&nbsp;&nbsp;&nbsp;&nbsp;{chapter.title}&nbsp;&nbsp;&nbsp;&nbsp;</>
                  )}
                </span>
              </Button>
              {isMenuOpen && (
                <ul className={dropdownMenuClass}>
                  <li className={menuItemLiClass}>
                    <Link to={`/book/${bookId}`} className={menuItemClass}>
                      <BookOpen size={16} />
                      Back to Book
                    </Link>
                  </li>
                  <li className={menuItemLiClass}>
                    <Button
                      variant="ghost"
                      fullWidth
                      css={menuItemStyles}
                      onClick={() => {
                        setIsDownloadDialogOpen(true);
                        setIsMenuOpen(false);
                      }}
                    >
                      <Download size={16} />
                      Download EPUB
                    </Button>
                  </li>
                  <li className={menuItemLiClass}>
                    <Button
                      variant="ghost"
                      fullWidth
                      css={menuItemStyles}
                      onClick={() => {
                        void handleDeleteFromHere();
                        setIsMenuOpen(false);
                      }}
                      style={{ color: "var(--colors-error)" }}
                    >
                      <Trash2 size={16} />
                      Delete from here
                    </Button>
                  </li>
                  {chapter.url && (
                    <li className={menuItemLiClass}>
                      <Button
                        as="a"
                        href={chapter.url}
                        target="_blank"
                        rel="noreferrer"
                        variant="ghost"
                        fullWidth
                        css={menuItemStyles}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <ExternalLink size={16} />
                        Open Source
                      </Button>
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <Button
            variant="ghost"
            onClick={handleNext}
            disabled={!chapter.next_id}
            css={navSectionSideStyles}
            aria-label="Next Chapter"
          >
            <ArrowRight size={24} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className={contentClass} data-debug-id="chapter-content">
        {paragraphGroups.map((group, groupIdx) => {
          const isHeader = group.segments.some((s) => s.isHeader);
          const Tag = isHeader ? "h1" : "p";

          return (
            <Tag key={groupIdx} className={isHeader ? headerClass : paragraphClass}>
              {group.segments
                .filter((segment) => segment.text.trim().length > 0)
                .map((segment, i) => {
                  const absoluteIndex = processedSegments.findIndex((s) => s === segment);
                  const isActive = segment.sourceIndex === currentIndex && isCurrentChapterPlaying;

                  const firstNonEmptyIdx = group.segments.findIndex(
                    (s) => s.text.trim().length > 0,
                  );
                  const needsSpace = segment.isNewSentence && i > firstNonEmptyIdx;

                  return (
                    <span
                      key={absoluteIndex}
                      ref={isActive ? activeSentenceRef : undefined}
                      className={sentence({ active: isActive })}
                      onClick={() => handlePlayChapter(segment.sourceIndex ?? -1)}
                    >
                      {needsSpace ? " " : ""}
                      {segment.text}
                    </span>
                  );
                })}
            </Tag>
          );
        })}
      </div>

      {/* Download Dialog */}
      {isDownloadDialogOpen && (
        <div className={dialogOverlayClass}>
          <div className={dialogBoxClass}>
            <div className={dialogHeaderClass}>
              <h3 className={dialogTitleClass}>Download EPUB</h3>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setIsDownloadDialogOpen(false)}
                css={{ padding: 0 }}
              >
                <X size={24} />
              </Button>
            </div>
            <p className={dialogDescriptionClass}>
              Select the maximum number of chapters to include:
            </p>
            <div className={dialogBodyClass}>
              <label className={dialogLabelClass}>Max Chapters</label>
              <Input
                type="number"
                value={downloadLimit}
                onChange={(e: any) => setDownloadLimit(parseInt(e.target.value) || 0)}
              />
            </div>
            <div className={dialogActionsClass}>
              <Button variant="ghost" onClick={() => setIsDownloadDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleDownloadClick}>Download</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
